import { memo, useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FileOutput, Upload, Loader2, Search, ChevronLeft, ChevronRight, Clock, Camera, Plus, Pencil, Trash2, Paperclip, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { AuthThumb, Lightbox, UploadBtn } from './FacturaDialog';
import { lei, fmtD } from './CheltuieliFurnizoriTab';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const MONTHS = ['', 'Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie', 'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'];
const sum = (rows, k) => Math.round(rows.reduce((a, r) => a + (r[k] || 0), 0) * 100) / 100;
const withUrl = (a) => ({ ...a, url: `${API_URL}/facturi-emise/fisiere/${a.id}` });
const EMPTY = { nr_factura: '', serie: '', client: '', cui: '', data: new Date().toISOString().slice(0, 10), scadent: '', baza_tva: '', tva: '', total: '', neincasat: '', inf_suplm: '' };
const num = (v) => parseFloat(String(v ?? '').replace(',', '.')) || 0;

const InvoiceForm = memo(function InvoiceForm({ initial, onSaved, onCancel, clients }) {
  const [f, setF] = useState({ ...EMPTY, ...Object.fromEntries(Object.entries(initial || {}).map(([k, v]) => [k, v ?? ''])) });
  const [busy, setBusy] = useState(false);
  const saga = initial?.sursa === 'saga';
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const autoTotal = () => { const b = num(f.baza_tva), t = num(f.tva); if (b || t) setF((s) => ({ ...s, total: String(Math.round((b + t) * 100) / 100) })); };
  const autoTva = () => { const b = num(f.baza_tva); if (b && !f.tva) setF((s) => ({ ...s, tva: String(Math.round(b * 0.21 * 100) / 100), total: String(Math.round(b * 1.21 * 100) / 100) })); };
  const submit = async (e) => {
    e.preventDefault();
    const body = { ...f, baza_tva: num(f.baza_tva), tva: num(f.tva), total: num(f.total), neincasat: num(f.neincasat), scadent: f.scadent || null };
    if (!body.nr_factura.trim() || !body.client.trim()) return toast.error('Numărul facturii și clientul sunt obligatorii');
    if (!body.total) return toast.error('Totalul este obligatoriu');
    setBusy(true);
    try {
      const r = initial?.id ? await axios.put(`${API_URL}/facturi-emise/${initial.id}`, body, auth()) : await axios.post(`${API_URL}/facturi-emise`, body, auth());
      toast.success(initial?.id ? 'Factură actualizată' : 'Factură adăugată'); onSaved(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Eroare la salvare'); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="factura-emisa-form">
      {saga && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">Factură importată din Saga — se pot modifica doar scadența și observațiile.</p>}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div><Label className="text-xs">Serie</Label><Input className="h-8" value={f.serie} onChange={set('serie')} disabled={saga} data-testid="fe-serie" /></div>
        <div><Label className="text-xs">Nr. factură *</Label><Input className="h-8" value={f.nr_factura} onChange={set('nr_factura')} disabled={saga} data-testid="fe-nr" /></div>
        <div><Label className="text-xs">Data *</Label><Input className="h-8" type="date" value={f.data} onChange={set('data')} disabled={saga} data-testid="fe-data" /></div>
        <div><Label className="text-xs">Scadență</Label><Input className="h-8" type="date" value={f.scadent} onChange={set('scadent')} data-testid="fe-scadent" /></div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="col-span-2"><Label className="text-xs">Client *</Label><Input className="h-8" list="fe-clienti" value={f.client} onChange={set('client')} disabled={saga} data-testid="fe-client" />
          <datalist id="fe-clienti">{clients.map((c) => <option key={c} value={c} />)}</datalist></div>
        <div><Label className="text-xs">CUI</Label><Input className="h-8" value={f.cui} onChange={set('cui')} disabled={saga} data-testid="fe-cui" /></div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div><Label className="text-xs">Bază (lei)</Label><Input className="h-8" inputMode="decimal" value={f.baza_tva} onChange={set('baza_tva')} onBlur={autoTva} disabled={saga} data-testid="fe-baza" /></div>
        <div><Label className="text-xs">TVA (lei)</Label><Input className="h-8" inputMode="decimal" value={f.tva} onChange={set('tva')} onBlur={autoTotal} disabled={saga} data-testid="fe-tva" /></div>
        <div><Label className="text-xs">Total *</Label><Input className="h-8 font-semibold" inputMode="decimal" value={f.total} onChange={set('total')} disabled={saga} data-testid="fe-total" /></div>
        <div><Label className="text-xs">Neîncasat (lei)</Label><Input className="h-8" inputMode="decimal" value={f.neincasat} onChange={set('neincasat')} disabled={saga} data-testid="fe-neincasat" /></div>
      </div>
      <div><Label className="text-xs">Observații</Label><Input className="h-8" value={f.inf_suplm} onChange={set('inf_suplm')} data-testid="fe-obs" /></div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} data-testid="fe-cancel">Anulează</Button>
        <Button type="submit" size="sm" disabled={busy} data-testid="fe-submit">{busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} {initial?.id ? 'Salvează' : 'Adaugă factura'}</Button>
      </div>
    </form>
  );
});

const IssuedDialog = memo(function IssuedDialog({ id, onClose, onChanged, canEdit, clients }) {
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(null);
  const load = useCallback(async () => {
    try { const r = await axios.get(`${API_URL}/facturi-emise/${id}`, auth()); setD(r.data); }
    catch { toast.error('Factura nu a putut fi încărcată'); onClose(); }
  }, [id, onClose]);
  useEffect(() => { if (id) { setD(null); setEdit(false); load(); } }, [id, load]);
  const run = async (fn, ok) => { setBusy(true); try { await fn(); if (ok) toast.success(ok); load(); onChanged?.(); } catch (e) { toast.error(e.response?.data?.detail || 'Eroare'); } finally { setBusy(false); } };
  const upload = (f) => run(async () => { const fd = new FormData(); fd.append('file', f); await axios.post(`${API_URL}/facturi-emise/${id}/atasamente`, fd, auth()); }, 'Fișier adăugat');
  const delAtt = (a) => window.confirm('Ștergi acest fișier?') && run(() => axios.delete(`${API_URL}/facturi-emise/atasamente/${a.id}`, auth()), 'Fișier șters');
  const delInv = () => window.confirm(`Ștergi factura ${d.nr_factura} (${d.client})?`) && run(async () => { await axios.delete(`${API_URL}/facturi-emise/${id}`, auth()); onClose(); }, 'Factură ștearsă');
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="factura-emisa-dialog">
        {!d ? <div className="flex items-center gap-2 text-slate-500 p-6"><Loader2 className="w-4 h-4 animate-spin" /> Se încarcă...</div> : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base">Factura {d.serie ? `${d.serie} ` : ''}{d.nr_factura} <span className="text-slate-400 font-normal">· {d.client}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase ${d.sursa === 'saga' ? 'bg-slate-100 text-slate-600' : 'bg-blue-50 text-blue-700'}`}>{d.sursa}</span></DialogTitle>
              <DialogDescription>Data {fmtD(d.data)} · scadență {fmtD(d.scadent)}{d.cui ? ` · CUI ${d.cui}` : ''}{d.inf_suplm ? ` · ${d.inf_suplm}` : ''}</DialogDescription>
            </DialogHeader>
            {edit ? <InvoiceForm initial={d} clients={clients} onCancel={() => setEdit(false)} onSaved={() => { setEdit(false); load(); onChanged?.(); }} /> : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm" data-testid="fe-sume">
                  {[['Bază', lei(d.baza_tva), ''], ['TVA', lei(d.tva), ''], ['Total', lei(d.total), 'font-semibold'], ['Neîncasat', lei(d.neincasat), d.neincasat > 0 ? 'text-red-600 font-semibold' : 'text-green-700']].map(([l, v, c]) => (
                    <div key={l} className="rounded-lg border bg-white px-3 py-2"><div className="text-[11px] uppercase text-slate-400">{l}</div><div className={`font-medium ${c}`}>{v} lei</div></div>
                  ))}
                </div>
                {canEdit && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEdit(true)} data-testid="fe-edit-btn"><Pencil className="w-4 h-4 mr-1" /> Editează</Button>
                    {d.sursa === 'manual' && <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={delInv} disabled={busy} data-testid="fe-delete-btn"><Trash2 className="w-4 h-4 mr-1" /> Șterge</Button>}
                  </div>
                )}
              </>
            )}
            <section className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold flex items-center gap-1.5"><Paperclip className="w-4 h-4" /> Poze / PDF factură <span className="text-slate-400 font-normal">({d.atasamente.length})</span></h4>
                {canEdit && <UploadBtn onFile={upload} busy={busy} label="Adaugă poză / PDF" testId="fe-upload" />}
              </div>
              {d.atasamente.length === 0 ? <p className="text-xs text-slate-400">Niciun fișier atașat.</p> : (
                <div className="flex flex-wrap gap-2" data-testid="fe-atasamente">{d.atasamente.map((a) => <AuthThumb key={a.id} att={withUrl(a)} onOpen={setView} onDelete={canEdit ? delAtt : null} />)}</div>
              )}
            </section>
          </>
        )}
        <Lightbox att={view} onClose={() => setView(null)} />
      </DialogContent>
    </Dialog>
  );
});

const FacturiEmiseTab = memo(function FacturiEmiseTab({ canEdit = true }) {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [imports, setImports] = useState([]);
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState('');
  const [unpaid, setUnpaid] = useState(false);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [adding, setAdding] = useState(false);
  const fileRef = useRef(null);
  const luna = `${year}-${String(month).padStart(2, '0')}`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [f, i, c] = await Promise.all([
        axios.get(`${API_URL}/facturi-emise`, { ...auth(), params: { luna, q: q || undefined, neincasate: unpaid || undefined } }),
        axios.get(`${API_URL}/facturi-emise/imports`, auth()),
        axios.get(`${API_URL}/facturi-emise/clienti`, auth()),
      ]);
      setData(f.data); setImports(i.data); setClients(c.data);
    } catch { toast.error('Nu s-au putut încărca facturile emise'); }
    finally { setLoading(false); }
  }, [luna, q, unpaid]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const shift = (d) => { let m = month + d, y = year; if (m < 1) { m = 12; y -= 1; } else if (m > 12) { m = 1; y += 1; } setMonth(m); setYear(y); };
  const onFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append('file', f);
      const d = (await axios.post(`${API_URL}/facturi-emise/import`, fd, auth())).data;
      toast.success(`${d.rows} rânduri · ${d.added} noi · ${d.skipped} existente (sărite)${d.updated ? ` · ${d.updated} actualizate la încasare` : ''}`, { duration: 7000 });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Eroare la import', { duration: 10000 }); }
    finally { setImporting(false); }
  };

  const rows = data?.rows || [];
  const t = data?.totals || {};
  const last = imports[0];

  return (
    <div className="space-y-4" data-testid="facturi-emise-tab">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2"><FileOutput className="w-5 h-5 text-slate-600" /> Facturi emise</h2>
          <p className="text-sm text-slate-500">Facturile emise către clienți: import din Saga (fără dublare) sau adăugare manuală. Click pe o factură pentru detalii și poze/PDF.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 border rounded-lg px-1 bg-white">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => shift(-1)} data-testid="fe-prev-month"><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-sm font-medium min-w-[140px] text-center" data-testid="fe-month-label">{MONTHS[month]} {year}</span>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => shift(1)} data-testid="fe-next-month"><ChevronRight className="w-4 h-4" /></Button>
          </div>
          {canEdit && (
            <>
              <input ref={fileRef} type="file" accept=".xls,.xlsx,.ods,.csv" className="hidden" onChange={onFile} data-testid="fe-file-input" />
              <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="fe-import-btn">
                {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} Import Saga
              </Button>
              <Button onClick={() => setAdding(true)} data-testid="fe-add-btn"><Plus className="w-4 h-4 mr-2" /> Adaugă factură</Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="fe-totals">
        {[['Facturi', data?.count ?? 0, '', ''], ['Bază', lei(t.baza_tva), 'lei', ''], ['TVA', lei(t.tva), 'lei', ''], ['Total', lei(t.total), 'lei', ''], ['Neîncasat', lei(t.neincasat), 'lei', t.neincasat > 0 ? 'text-red-600' : '']].map(([l, v, u, c]) => (
          <div key={l} className="rounded-lg border bg-white px-4 py-3">
            <div className="text-xs uppercase tracking-wide text-slate-400">{l}</div>
            <div className={`text-lg font-semibold ${c}`}>{v} <span className="text-xs font-normal text-slate-400">{u}</span></div>
          </div>
        ))}
      </div>
      {last && <div className="flex items-center gap-1 text-xs text-slate-500"><Clock className="w-3.5 h-3.5" /> Ultimul import: {new Date(last.imported_at).toLocaleString('ro-RO')} · {last.filename} ({last.added} noi / {last.skipped} sărite)</div>}

      <div className="flex flex-wrap items-center gap-2" data-testid="fe-filters">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-2 top-2" />
          <Input className="h-8 w-64 pl-8 pr-7 text-sm bg-white" placeholder="Nr. factură / client / CUI..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="fe-search" />
          {q && <button type="button" className="absolute right-1.5 top-1.5 p-0.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100" onClick={() => setQ('')} title="Șterge căutarea" data-testid="fe-search-clear"><X className="w-4 h-4" /></button>}
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer"><Checkbox checked={unpaid} onCheckedChange={(v) => setUnpaid(!!v)} data-testid="fe-unpaid-filter" /> doar neîncasate</label>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? <div className="flex items-center gap-2 text-slate-500 p-8"><Loader2 className="w-4 h-4 animate-spin" /> Se încarcă...</div>
            : rows.length === 0 ? <div className="p-10 text-center text-sm text-slate-500" data-testid="fe-empty">Nicio factură emisă pentru filtrele alese.</div> : (
            <div className="overflow-x-auto">
              <Table data-testid="fe-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead><TableHead>Nr. factură</TableHead><TableHead>Client</TableHead>
                    <TableHead className="text-right">Bază</TableHead><TableHead className="text-right">TVA</TableHead><TableHead className="text-right">Total</TableHead>
                    <TableHead>Scadent</TableHead><TableHead className="text-right">Neîncasat</TableHead>
                    <TableHead className="text-center"><Camera className="w-4 h-4 inline" /></TableHead><TableHead>Sursă</TableHead><TableHead>Observații</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setOpenId(r.id)} data-testid={`fe-row-${r.nr_factura}`}>
                      <TableCell className="text-sm whitespace-nowrap">{fmtD(r.data)}</TableCell>
                      <TableCell className="font-mono text-xs whitespace-nowrap">{r.serie ? `${r.serie} ` : ''}{r.nr_factura}</TableCell>
                      <TableCell className="text-sm">{r.client}{r.cui && <span className="block text-[10px] text-slate-400">CUI {r.cui}</span>}</TableCell>
                      <TableCell className="text-right text-sm whitespace-nowrap">{lei(r.baza_tva)}</TableCell>
                      <TableCell className="text-right text-sm whitespace-nowrap">{lei(r.tva)}</TableCell>
                      <TableCell className={`text-right text-sm font-medium whitespace-nowrap ${r.total < 0 ? 'text-blue-700' : ''}`}>{lei(r.total)}</TableCell>
                      <TableCell className="text-sm whitespace-nowrap">{fmtD(r.scadent)}</TableCell>
                      <TableCell className={`text-right text-sm whitespace-nowrap ${r.neincasat > 0 ? 'text-red-600 font-semibold' : 'text-slate-400'}`}>{r.neincasat > 0 ? lei(r.neincasat) : '—'}</TableCell>
                      <TableCell className="text-center text-xs text-slate-500">{r.nr_poze > 0 ? <span className="inline-flex items-center gap-0.5" data-testid={`fe-poze-${r.nr_factura}`}><Camera className="w-3.5 h-3.5" />{r.nr_poze}</span> : ''}</TableCell>
                      <TableCell><span className={`text-[10px] px-1.5 py-0.5 rounded uppercase ${r.sursa === 'saga' ? 'bg-slate-100 text-slate-600' : 'bg-blue-50 text-blue-700'}`}>{r.sursa}</span></TableCell>
                      <TableCell className="text-xs text-slate-500 max-w-[220px] truncate" title={r.inf_suplm || ''}>{r.inf_suplm || ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow className="bg-slate-100 font-semibold" data-testid="fe-table-totals">
                    <TableCell colSpan={3} className="text-sm">TOTAL ({rows.length} facturi)</TableCell>
                    <TableCell className="text-right text-sm whitespace-nowrap">{lei(sum(rows, 'baza_tva'))}</TableCell>
                    <TableCell className="text-right text-sm whitespace-nowrap">{lei(sum(rows, 'tva'))}</TableCell>
                    <TableCell className="text-right text-sm whitespace-nowrap">{lei(sum(rows, 'total'))}</TableCell>
                    <TableCell />
                    <TableCell className={`text-right text-sm whitespace-nowrap ${sum(rows, 'neincasat') > 0 ? 'text-red-600' : 'text-slate-400'}`}>{lei(sum(rows, 'neincasat'))}</TableCell>
                    <TableCell colSpan={3} />
                  </TableRow>
                </TableFooter>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent className="max-w-2xl" data-testid="fe-add-dialog">
          <DialogHeader><DialogTitle>Adaugă factură emisă</DialogTitle><DialogDescription>Introdu manual o factură emisă către un client. Poza/PDF-ul se atașează după salvare, din detaliile facturii.</DialogDescription></DialogHeader>
          <InvoiceForm clients={clients} onCancel={() => setAdding(false)} onSaved={(r) => { setAdding(false); load(); setOpenId(r.id); }} />
        </DialogContent>
      </Dialog>
      <IssuedDialog id={openId} onClose={() => setOpenId(null)} onChanged={load} canEdit={canEdit} clients={clients} />
    </div>
  );
});

export default FacturiEmiseTab;
